import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { usePanes, useIsPaneOpen } from "../main/panes/usePanes";
import TaskPane from "./taskPane/TaskPane";

export const taskUrlParameter = "taskId";

/**
 * Binds `?taskId=` to a task pane on the stage, in both directions: the parameter opens the pane, and
 * closing the pane drops the parameter. Emails and notifications link to a task this way, so the binding
 * has to work wherever the link lands.
 *
 * It lives with the task feature rather than in the rail, which knows nothing about tasks and should not
 * start to. Every page that wants url-addressable tasks calls this; it needs nothing from the page.
 *
 * Stepping to a sibling task inside the pane stays a local move and deliberately does not touch the url,
 * exactly as it did while this was a dialog, so the "back to opened task" arrow still has an opened task
 * to return to.
 */
export function useTaskUrlBinding() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { open } = usePanes();
    const taskId = searchParams.get(taskUrlParameter) ?? "";

    // Read from the panes rather than handed to the pane as a callback. The pane outlives this page, and a
    // callback holding this page's setSearchParams navigated back here when the pane closed on another page.
    // Following the panes from here instead means that once the page is gone, nothing is left to fire.
    const isTaskPaneOpen = useIsPaneOpen(TaskPane, (props) => props.taskId === taskId);

    // The task this hook last put on the stage. It stops a closed pane being reopened by the parameter that
    // is still on its way out.
    const openedTaskId = useRef<string | undefined>(undefined);
    // Whether that pane has been seen open. Until it has, a closed pane means the open is still pending,
    // behind the discard confirmation for the pane it replaces, not that the user closed it.
    const hasBeenShown = useRef(false);

    useEffect(() => {
        if (!taskId) {
            openedTaskId.current = undefined;
            return;
        }

        if (openedTaskId.current === taskId) {
            return;
        }

        openedTaskId.current = taskId;
        hasBeenShown.current = false;
        open(TaskPane, { taskId });
    }, [taskId, open]);

    useEffect(() => {
        if (!taskId || openedTaskId.current !== taskId) {
            return;
        }

        if (isTaskPaneOpen) {
            hasBeenShown.current = true;
            return;
        }

        if (hasBeenShown.current) {
            setSearchParams(
                (previous) => {
                    const next = new URLSearchParams(previous);
                    next.delete(taskUrlParameter);
                    return next;
                },
                { replace: true }
            );
        }
    }, [taskId, isTaskPaneOpen, setSearchParams]);

    return taskId;
}
